import { useTranslation } from 'react-i18next';
import { InfiosContainer, InfiosIcon, InfiosLink } from '@infios/uikit/react';
import type { RootCause } from '../../interfaces/chat';
import { useReferenceAction } from '../../context/ReferenceActionContext';
import styles from './RootCauseBlock.module.css';

interface RootCauseBlockProps {
  rootCauses: RootCause[];
}

/**
 * One block per root cause, sorted by `slaImpactRank` ascending (rank 1 = most
 * severe, shown first). Invariant #3: a cause never renders without its
 * `detail` alongside it — `detail` is a required field, so every entry here
 * always has one. Invariant #6: all causes render, never truncated.
 */
export function RootCauseBlock({ rootCauses }: RootCauseBlockProps) {
  const { t } = useTranslation();
  const onReference = useReferenceAction();
  if (rootCauses.length === 0) return null;

  const sorted = [...rootCauses].sort((a, b) => a.slaImpactRank - b.slaImpactRank);

  return (
    <div className={styles.wrap} data-testid="root-cause-block">
      <h4 className={styles.heading}>{t('ChatbotUI.OrderInsight.RootCauses')}</h4>
      <div className={styles.list}>
        {sorted.map((rc) => (
          <InfiosContainer key={rc.id} type="tile" className={styles.block}>
            <div className={styles.symptomRow}>
              <InfiosIcon name="alert-circle" className={styles.icon} />
              <span className={styles.symptom}>{rc.symptom}</span>
            </div>
            <p className={styles.cause}>{rc.cause}</p>
            <p className={styles.detail}>{rc.detail}</p>
            {rc.reference ? (
              <InfiosLink
                className={styles.refLink}
                showIcon
                onClick={() => onReference?.(rc.reference as NonNullable<RootCause['reference']>)}
              >
                {rc.reference.label ?? rc.reference.id}
              </InfiosLink>
            ) : null}
          </InfiosContainer>
        ))}
      </div>
    </div>
  );
}
